import keyBy from 'lodash.keyby';
import get from 'lodash.get';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import formatDropdownOptions from 'utils/format-dropdown-options';
import { OutputParametricSelector } from 'reselect';

import { AppState } from 'services/types';
// @ts-ignore
import {
    EquipmentState,
    EquipmentProps,
    EquipmentId,
    Equipment
} from './types';

const selectEquipment = (state: AppState): EquipmentState => state.equipment;
const selectEquipmentId = (
    state: AppState,
    props: EquipmentProps
): EquipmentId => props.equipmentId;

const selectEquipmentArray = createDeepEqualSelector(
    selectEquipment,
    (equipment) => Object.values(equipment)
);

const selectEquipmentKeyedById = createDeepEqualSelector(
    selectEquipmentArray,
    (equipment) => keyBy(equipment, 'id')
);

const selectSingleEquipment = createDeepEqualSelector(
    selectEquipmentKeyedById,
    selectEquipmentId,
    (equipment, equipmentId): Equipment => get(equipment, equipmentId)
);

const selectActiveEquipment = createDeepEqualSelector(
    selectEquipmentArray,
    (equipment): Equipment[] => equipment.filter((eq) => eq.deletedAt === null)
);

const selectActiveSprayerEquipment = createDeepEqualSelector(
    selectActiveEquipment,
    (equipment): Equipment[] =>
        equipment.filter((eq) => eq.equipmentType === 'SPRAYER')
);

const selectSprayerEquipmentOptions = createDeepEqualSelector(
    selectActiveSprayerEquipment,
    (equipment) =>
        formatDropdownOptions(equipment, {
            keyField: 'id',
            textField: ({ name, tankVolume }) => `${name} (${tankVolume} L)`
        })
);

const selectActiveTractorEquipment = createDeepEqualSelector(
    selectActiveEquipment,
    (equipment): Equipment[] =>
        equipment.filter((eq) => eq.equipmentType === 'TRACTOR')
);

const selectTractorEquipmentOptions = createDeepEqualSelector(
    selectActiveTractorEquipment,
    (equipment) =>
        formatDropdownOptions(equipment, {
            keyField: 'id',
            textField: 'name'
        })
);

const selectActiveNozzleSetEquipment = createDeepEqualSelector(
    selectActiveEquipment,
    (equipment): Equipment[] =>
        equipment.filter((eq) => eq.equipmentType === 'NOZZLESET')
);

const selectNozzleSetEquipmentOptions = createDeepEqualSelector(
    selectActiveNozzleSetEquipment,
    (equipment) =>
        formatDropdownOptions(equipment, {
            keyField: 'id',
            textField: ({ name, nozzleType }) => `${name} (${nozzleType})`
        })
);

const makeSelectEquipmentFieldValue = <K extends keyof Equipment>(
    fieldName: K
): OutputParametricSelector<
    AppState,
    EquipmentProps,
    Equipment[K],
    (res: Equipment) => Equipment[K]
> =>
    createDeepEqualSelector(selectSingleEquipment, (singleEquipment) => {
        return get(singleEquipment, fieldName);
    });

export {
    selectEquipment,
    selectEquipmentId,
    selectEquipmentArray,
    selectEquipmentKeyedById,
    selectSingleEquipment,
    selectActiveEquipment,
    selectActiveSprayerEquipment,
    selectSprayerEquipmentOptions,
    selectActiveTractorEquipment,
    selectTractorEquipmentOptions,
    selectActiveNozzleSetEquipment,
    selectNozzleSetEquipmentOptions,
    makeSelectEquipmentFieldValue
};
